import { Stack, Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import { ExpandableSection } from 'core/components';
import { QuoteEntityType } from 'quotes/enums';
import { IBaseQuote, IQuoteFile } from 'quotes/types';
import { Color, DecoratorPosition, Direction, Level } from 'theme/enums';

import { DocumentCard } from './DocumentCard';
import { QuoteFileSource } from './enums';

interface IProps {
  source: QuoteFileSource;
  records: IQuoteFile[];
  quote: IBaseQuote;
  entityType: QuoteEntityType;
  filteredIds: Set<number>;
  onRemoveFile: (fileId: number) => Promise<void>;
}

export const DocumentSection = ({
  source,
  records,
  quote,
  entityType,
  filteredIds,
  onRemoveFile,
}: IProps) => {
  const { t } = useTranslation();

  const getTitle = (source: QuoteFileSource) => {
    switch (source) {
      case QuoteFileSource.UPLOAD:
        return t('labelManuallyUploaded');
      case QuoteFileSource.BOM_IMPORT:
        return t('labelBomImports');
      case QuoteFileSource.VENDOR_QUOTE_IMPORT:
        return t('labelImportedVendorQuotes');
      case QuoteFileSource.DOWNLOAD:
        return t('labelDownloaded');
      case QuoteFileSource.SPA_SENT:
        return t('labelSentAsSpa');
    }
  };

  const noFilesMessage = (
    <Typography color={Color.NEUTRAL} level={Level.TITLE_SM} fontStyle="italic" sx={{ padding: 1 }}>
      {records?.length ? t('messageNoMatchingFiles') : t('messageNoFilesAttached')}
    </Typography>
  );

  return (
    <ExpandableSection
      title={getTitle(source)}
      defaultExpanded={true}
      iconPosition={DecoratorPosition.START}
    >
      {records.filter((file) => filteredIds.has(file.id)).length > 0 ? (
        <Stack
          direction={Direction.ROW}
          sx={{
            flexWrap: 'wrap',
            minWidth: 0,
            gap: 2,
            padding: 1,
          }}
        >
          {records
            .filter((file) => filteredIds.has(file.id))
            .map((quoteFile) => (
              <DocumentCard
                key={quoteFile.id}
                quoteFile={quoteFile}
                entityType={entityType}
                quoteType={quote.type}
                onRemoveFile={onRemoveFile}
              />
            ))}
        </Stack>
      ) : (
        noFilesMessage
      )}
    </ExpandableSection>
  );
};
